import api from './api-new';

export interface MigrationResult {
  success: boolean;
  message: string;
  migratedCount?: number;
}

export class DataMigrationService {
  static async migratePatients(): Promise<MigrationResult> {
    try {
      const localPatients = localStorage.getItem('patients');
      if (!localPatients) {
        return { success: true, message: 'No local patients to migrate' };
      }

      const patients = JSON.parse(localPatients);
      let migratedCount = 0;

      for (const patient of patients) {
        try {
          await api.patients.create({
            name: patient.name,
            species: patient.species,
            breed: patient.breed,
            age: patient.age,
            weight: patient.weight,
            owner_name: patient.ownerName,
            owner_phone: patient.ownerPhone,
            medical_history: patient.medicalHistory || '',
            allergies: patient.allergies || '',
          });
          migratedCount++;
        } catch (error) {
          console.error(`Failed to migrate patient ${patient.name}:`, error);
        }
      }

      return { 
        success: true, 
        message: `Migrated ${migratedCount} patients successfully`,
        migratedCount 
      };
    } catch (error) {
      return { 
        success: false, 
        message: `Failed to migrate patients: ${error.message}` 
      };
    }
  }

  static async migrateServices(): Promise<MigrationResult> {
    try {
      const localServices = localStorage.getItem('services');
      if (!localServices) {
        return { success: true, message: 'No local services to migrate' };
      }

      const services = JSON.parse(localServices);
      let migratedCount = 0;

      for (const service of services) {
        try {
          await api.services.create({
            name: service.name,
            description: service.description,
            price: service.price,
            duration: service.duration,
            category: service.category,
          });
          migratedCount++;
        } catch (error) {
          console.error(`Failed to migrate service ${service.name}:`, error);
        }
      }

      return { 
        success: true, 
        message: `Migrated ${migratedCount} services successfully`,
        migratedCount 
      };
    } catch (error) {
      return { 
        success: false, 
        message: `Failed to migrate services: ${error.message}` 
      };
    }
  }

  static async migrateProducts(): Promise<MigrationResult> {
    try {
      const localProducts = localStorage.getItem('products');
      if (!localProducts) {
        return { success: true, message: 'No local products to migrate' };
      }

      const products = JSON.parse(localProducts);
      let migratedCount = 0;

      for (const product of products) {
        try {
          await api.products.create({
            name: product.name,
            description: product.description,
            price: product.price,
            stock: product.stock,
            category: product.category,
            image: product.image,
          });
          migratedCount++;
        } catch (error) {
          console.error(`Failed to migrate product ${product.name}:`, error);
        }
      }

      return { 
        success: true, 
        message: `Migrated ${migratedCount} products successfully`,
        migratedCount 
      };
    } catch (error) {
      return { 
        success: false, 
        message: `Failed to migrate products: ${error.message}` 
      };
    }
  }

  static async migrateVets(): Promise<MigrationResult> {
    try {
      const localVets = localStorage.getItem('vets');
      if (!localVets) {
        return { success: true, message: 'No local vets to migrate' };
      }

      const vets = JSON.parse(localVets);
      let migratedCount = 0;

      for (const vet of vets) {
        try {
          await api.vets.create({
            name: vet.name,
            specialty: vet.specialty,
            experience: vet.experience,
            rating: vet.rating,
            image: vet.image,
            available: vet.available,
          });
          migratedCount++;
        } catch (error) {
          console.error(`Failed to migrate vet ${vet.name}:`, error);
        }
      }

      return { 
        success: true, 
        message: `Migrated ${migratedCount} vets successfully`,
        migratedCount 
      };
    } catch (error) {
      return { 
        success: false, 
        message: `Failed to migrate vets: ${error.message}` 
      };
    }
  }

  static async migrateAll(): Promise<{
    patients: MigrationResult;
    services: MigrationResult;
    products: MigrationResult;
    vets: MigrationResult;
  }> {
    const results = {
      patients: await this.migratePatients(),
      services: await this.migrateServices(),
      products: await this.migrateProducts(),
      vets: await this.migrateVets(),
    };

    return results;
  }

  static clearLocalStorage(): void {
    const keys = ['patients', 'services', 'products', 'vets', 'appointments', 'orders'];
    keys.forEach(key => localStorage.removeItem(key));
  }
}

export default DataMigrationService;